import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { FormattedMessage, useIntl } from 'dibs-react-intl';

import styles from './PdpSharedDealerInfo.scss';

const MINIMUM_ORDER_QUANTITY = 5;

const formatResponseTime = value => {
    const responseTime = parseInt(value, 10);
    if (isNaN(responseTime)) {
        return null;
    }

    if (responseTime > 168) {
        return (
            <FormattedMessage id="PdpSharedDealerInfo.response_time_week" defaultMessage="A week" />
        );
    }

    if (responseTime > 48) {
        return (
            <FormattedMessage
                id="PdpSharedDealerInfo.response_time_several_days"
                defaultMessage="Several days"
            />
        );
    }

    if (responseTime > 23) {
        return (
            <FormattedMessage
                id="PdpSharedDealerInfo.response_time_few_days"
                defaultMessage="1 to 2 days"
            />
        );
    }

    return (
        <FormattedMessage
            id="PdpSharedDealerInfo.response_time_value"
            defaultMessage="{responseTime, plural,
              =0 {'<'1 hour}
              one {1 hour}
              other {{responseTime} hours}
            }"
            values={{ responseTime }}
        />
    );
};

function PdpSharedDealerInfoComponent({ seller }) {
    const { formatNumber } = useIntl();
    const sellerOrderQuantity = seller?.sellerOrderQuantity;
    const establishmentYear = seller?.liveStorefrontProfile?.establishmentYear;
    const tradeAssociations = seller?.tradeAssociations || [];
    const dealerSince = new Date(seller?.agreementStart).getUTCFullYear();
    const responseTime = formatResponseTime(seller?.analytics?.metrics?.responseTime?.value);

    return (
        <Fragment>
            {establishmentYear && (
                <div className={styles.info} data-tn="dealer-establishmentYear">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.establishmentYear"
                        defaultMessage="Established in {establishmentYear}"
                        values={{ establishmentYear }}
                    />
                </div>
            )}
            {dealerSince && (
                <div className={styles.info} data-tn="dealer-since">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.since"
                        defaultMessage="1stDibs seller since {dealerSince}"
                        values={{ dealerSince }}
                    />
                </div>
            )}
            {sellerOrderQuantity && sellerOrderQuantity >= MINIMUM_ORDER_QUANTITY && (
                <div className={styles.info} data-tn="dealer-orderQuantity">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.sellerOrderQuantity"
                        defaultMessage="{sellerOrderQuantity} sales on 1stDibs"
                        values={{ sellerOrderQuantity: formatNumber(sellerOrderQuantity) }}
                    />
                </div>
            )}
            {responseTime && (
                <div className={styles.info} data-tn="dealer-response-time">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.response_time"
                        defaultMessage="Typical response time: {responseTime}"
                        values={{ responseTime }}
                    />
                </div>
            )}
            {!!tradeAssociations.length && (
                <div className={styles.associations} data-tn="dealer-associations">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.associations"
                        defaultMessage="Associations"
                    />

                    <div className={styles.associationsList}>
                        {tradeAssociations.map(({ displayName, serviceId }) => (
                            <span key={serviceId} className={styles.info}>
                                {displayName}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </Fragment>
    );
}

PdpSharedDealerInfoComponent.propTypes = {
    seller: PropTypes.object.isRequired,
};

export const PdpSharedDealerInfo = createFragmentContainer(PdpSharedDealerInfoComponent, {
    seller: graphql`
        fragment PdpSharedDealerInfo_seller on Seller {
            agreementStart
            sellerOrderQuantity
            analytics {
                metrics {
                    responseTime {
                        value
                    }
                }
            }
            tradeAssociations {
                serviceId
                displayName
            }
            liveStorefrontProfile {
                establishmentYear
            }
        }
    `,
});
